import React, { useState, useRef } from 'react';
import { Upload, Trash2, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';

const ImageUploadField = ({
  label,
  value,
  onChange,
  maxWidth = 800,
  quality = 0.82,
  placeholder = 'https://...',
  helperText = 'موبائل یا کمپیوٹر سے تصویر منتخب کریں یا انٹرنیٹ سے لنک پیسٹ کریں۔',
  previewShape = 'rect' // 'rect' | 'square' | 'circle'
}) => {
  const [mode, setMode] = useState('upload'); // 'upload' | 'url'
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('براہ کرم صرف تصویر (JPG, PNG, WebP) منتخب کریں۔');
      return;
    }

    setIsProcessing(true);

    // 1. First attempt direct upload to Hostinger server disk (api/upload.php)
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Dynamically resolve upload.php path for subfolder (e.g. /himalayan/) or root
      const uploadUrl = window.location.pathname.includes('/himalayan')
        ? '/himalayan/api/upload.php'
        : 'api/upload.php';

      const token = localStorage.getItem('himalayan_admin_token');
      const headers = token ? { 'Authorization': `Bearer ${token}`, 'X-Admin-Token': token } : {};

      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers,
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.url) {
          // Prepend base path if needed
          const finalUrl = window.location.pathname.includes('/himalayan') && !data.url.startsWith('/himalayan')
            ? `/himalayan/${data.url.replace(/^\//, '')}`
            : data.url;
          onChange(finalUrl);
          setIsProcessing(false);
          e.target.value = '';
          return;
        }
      }
    } catch (serverErr) {
      // Server upload unavailable (e.g. offline/local dev) -> fallback to client compression
    }

    // 2. Fallback: Compress on client side as Base64 DataURL
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        try {
          const maxDim = Math.min(maxWidth || 800, 800);
          let w = img.width;
          let h = img.height;
          if (w > maxDim || h > maxDim) {
            if (w >= h) {
              h = Math.round((h * maxDim) / w);
              w = maxDim;
            } else {
              w = Math.round((w * maxDim) / h);
              h = maxDim;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');

          const isPngOrSvg = file.type === 'image/png' || file.type === 'image/svg+xml' || file.type === 'image/x-icon';
          let compressed;
          if (isPngOrSvg) {
            // Keep transparent background for icons & logos
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            compressed = canvas.toDataURL('image/png');
          } else {
            // White background so transparent PNGs don't become black
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            compressed = canvas.toDataURL('image/jpeg', 0.75);
          }

          // If still over 120KB and is JPEG, downscale further
          if (compressed.length > 120000 && !isPngOrSvg) {
            const smallerCanvas = document.createElement('canvas');
            const scale = 0.7;
            smallerCanvas.width = Math.round(w * scale);
            smallerCanvas.height = Math.round(h * scale);
            const smallCtx = smallerCanvas.getContext('2d');
            smallCtx.fillStyle = '#FFFFFF';
            smallCtx.fillRect(0, 0, smallerCanvas.width, smallerCanvas.height);
            smallCtx.drawImage(canvas, 0, 0, smallerCanvas.width, smallerCanvas.height);
            compressed = smallerCanvas.toDataURL('image/jpeg', 0.65);
          }

          onChange(compressed);
        } catch (err) {
          console.error('Image compression error:', err);
          alert('تصویر پروسیس کرنے میں مسئلہ پیش آیا۔');
        } finally {
          setIsProcessing(false);
        }
      };
      img.onerror = () => {
        setIsProcessing(false);
        alert('تصویر لوڈ کرنے میں مسئلہ پیش آیا۔');
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      setIsProcessing(false);
      alert('فائل پڑھنے میں مسئلہ پیش آیا۔');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const previewClasses = previewShape === 'circle'
    ? 'w-20 h-20 rounded-full'
    : previewShape === 'square'
    ? 'w-20 h-20 rounded-2xl'
    : 'w-28 h-20 rounded-2xl';

  return (
    <div className="bg-gray-50/90 rounded-2xl p-4 border border-gray-200/90 space-y-3">
      {/* Header & Mode Switch */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-xs font-bold text-gray-800 font-urdu flex items-center gap-1.5">
          <ImageIcon className="w-4 h-4 text-pansar-700" />
          <span>{label}</span>
        </label>

        <div className="flex bg-white rounded-xl p-0.5 border border-gray-200 text-[11px] font-urdu shadow-2xs">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`px-3 py-1 rounded-lg transition font-bold cursor-pointer ${
              mode === 'upload'
                ? 'bg-pansar-800 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            موبائل / کمپیوٹر سے فائل
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`px-3 py-1 rounded-lg transition font-bold cursor-pointer ${
              mode === 'url'
                ? 'bg-pansar-800 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            انٹرنیٹ لنک (URL)
          </button>
        </div>
      </div>

      {/* Main Area: Preview + Upload/URL input */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        {/* Thumbnail Preview Box */}
        <div className={`relative ${previewClasses} overflow-hidden bg-white border-2 border-dashed border-gray-300 shrink-0 flex items-center justify-center shadow-xs`}>
          {value ? (
            <>
              <img
                src={value}
                alt="Preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  // If broken image
                  e.target.style.opacity = '0.3';
                }}
              />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute top-1 right-1 p-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow transition cursor-pointer"
                title="تصویر ہٹائیں"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          ) : (
            <div className="text-center p-2 text-gray-400">
              <ImageIcon className="w-6 h-6 mx-auto mb-1 text-gray-300" />
              <span className="text-[10px] font-urdu block">تصویر منتخب نہیں</span>
            </div>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex-1 w-full">
          {mode === 'upload' ? (
            <div className="space-y-1.5">
              <button
                type="button"
                disabled={isProcessing}
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-2.5 px-4 rounded-xl border border-dashed border-pansar-400 hover:border-pansar-700 bg-white hover:bg-pansar-50 text-pansar-900 font-bold text-xs font-urdu transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
              >
                <Upload className="w-4 h-4 text-pansar-700" />
                <span>{isProcessing ? 'تصویر تیار ہو رہی ہے...' : 'اپنے موبائل یا کمپیوٹر سے تصویر منتخب کریں'}</span>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <span className="text-[10px] text-gray-500 font-urdu block">
                {helperText}
              </span>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="relative">
                <input
                  type="url"
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={placeholder}
                  className="w-full p-2.5 pl-8 rounded-xl border border-gray-200 text-xs font-sans focus:border-pansar-700 focus:outline-none text-left dir-ltr bg-white"
                />
                <LinkIcon className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              </div>
              <span className="text-[10px] text-gray-500 font-urdu block">
                کسی بھی تصویر کا براہِ راست آن لائن لنک (URL) یہاں درج کریں۔
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploadField;
